import { useMemo } from 'react';
import { useIntl } from 'umi';

import {
  buildHorBarRenderList,
  calculateDodPercent,
  formatPercent,
  getStudioTrendDateConfig,
  normalizeStudioTrendPeriod,
  resolveHorBarYAxisDateFormat,
} from './_utils';
import { DEFAULT_SERIES_CONFIG } from './_constants';
import type {
  IStudioCardHorBarProps,
  IHorBarRenderListResult,
  ISeriesConfig,
} from './_type';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';

export function useStudioCardHorBar(props: IStudioCardHorBarProps) {
  const {
    title = 'Base Unit Sold',
    titleCn,
    subtitle,
    subtitleCn,
    headerHintByDateType,
    headerHint: legacyHeaderHint,
    metricKey = 'full_game_net_units',
    formatRule = 'bmk',
    xAxisFormat = 'bmk',
    yAxisDateFormat = 'MMM',
    data: propsData,
    dataKey = 'studioCardHorBar',
    seriesConfig = DEFAULT_SERIES_CONFIG,
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const displayTitle = getName(isEn, title, titleCn) || title;
  const displaySubtitle = getName(isEn, subtitle, subtitleCn);

  const serviceData = propsData?.[dataKey];
  const { data: rawData, errorMsg } = serviceData || {};
  /** Abnormal 默认 isLoading=true；无 data 槽时勿把 undefined 当加载中 */
  const chartLoading = Boolean(serviceData?.loading);
  const { card_list: cardList, trend_list: trendList, period: rawPeriod } = rawData || {};
  /** 与筛选器粒度一致：优先用 filter.date_type，避免接口 period 固定为 daily 导致 YTD/YTW/YTM 文案不切换 */
  const filterDateType = (
    propsData as { filter?: { date_type?: string; }; } | undefined
  )?.filter?.date_type;
  const period = useMemo(
    () => normalizeStudioTrendPeriod(filterDateType ?? rawPeriod),
    [filterDateType, rawPeriod],
  );
  const dateStr = useMemo(() => {
    if (period === 'weekly') {
      return 'W';
    }
    if (period === 'monthly') {
      return 'M';
    }
    return 'D';
  }, [period]);

  const displayHeaderHint = useMemo(() => {
    if (headerHintByDateType) {
      let entry = headerHintByDateType.daily;
      if (period === 'weekly') {
        entry = headerHintByDateType.weekly ?? headerHintByDateType.daily;
      } else if (period === 'monthly') {
        entry = headerHintByDateType.monthly ?? headerHintByDateType.daily;
      }
      if (!entry?.en && !entry?.cn) {
        return '';
      }
      return getName(isEn, entry.en, entry.cn ?? entry.en) || '';
    }
    if (legacyHeaderHint === 'ytRevenue') {
      return `(YT${dateStr} Revenue)`;
    }
    if (legacyHeaderHint && typeof legacyHeaderHint === 'string') {
      return legacyHeaderHint.replaceAll('{{dateStr}}', dateStr);
    }
    return '';
  }, [headerHintByDateType, period, isEn, legacyHeaderHint, dateStr]);
  const dateConfig = useMemo(() => {
    const axisDateFormat = resolveHorBarYAxisDateFormat(yAxisDateFormat, period);
    return getStudioTrendDateConfig(period, { axisDateFormat });
  }, [period, yAxisDateFormat]);

  /** 深拷贝避免引用抖动；毛/净指标 key 由 pageConfig + StudioCardTrendService 合并的 cardProps 决定 */
  const stableSeriesConfigKey = useMemo(() => JSON.stringify(seriesConfig || []), [seriesConfig]);
  const stableSeriesConfig = useMemo((): ISeriesConfig[] => {
    return JSON.parse(stableSeriesConfigKey || '[]');
  }, [stableSeriesConfigKey]);

  const cardItem = cardList?.find?.((c: any) => c.metrics_name === metricKey);
  const cardValue = cardItem?.value ?? '';
  const rate = formatPercent(calculateDodPercent(cardItem?.dod)?.percent);

  const renderList: IHorBarRenderListResult = useMemo(() => {
    if (!Array.isArray(trendList) || !trendList.length) {
      return { options: {}, dataSource: [], nodata: true };
    }
    const built = buildHorBarRenderList({
      trendList, seriesConfig: stableSeriesConfig, formatRule, xAxisFormat,
      isEn, period, dateConfig,
    });

    // 兜底：接口有值时不应进入 nodata（避免格式化/解析差异误判）
    const hasAnySeriesValue = trendList.some((item: any) =>
      stableSeriesConfig.some((cfg: any) => {
        const raw = item?.metrics?.[cfg.metricKey]?.value;
        if (raw === '' || raw === null || raw === undefined) return false;
        const num = Number(raw);
        return Number.isFinite(num);
      }),
    );

    if (hasAnySeriesValue && built.nodata) {
      return { ...built, nodata: false };
    }

    return built;
  }, [trendList, stableSeriesConfig, formatRule, xAxisFormat, isEn, period, dateConfig]);

  return {
    displayTitle,
    displaySubtitle,
    displayHeaderHint,
    cardValue,
    rate,
    chartLoading,
    errorMsg,
    renderList,
    formatRule,
  };
}
